import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ethers } from 'ethers';
import { ON_CHAIN_TOKEN_ADDRESSES, formatOnChainBalance } from '../lib/blockchain';
import {
  claimBbg,
  getWorldIdDiagnostic,
  getBbgClaimStatus,
  type ClaimStage,
  type WorldIdDiagnostic,
  type ClaimStatus,
} from '../lib/worldIdClaim';
import { formatErrorWithCode, formatMessageWithCode } from '../i18n';
import { walletAppConfig } from '../lib/config';
import { buildWalletOperationIdempotencyKey, recordWalletOperationBestEffort } from '../lib/walletActivity';

type ClaimModalProps = {
  isOpen: boolean;
  authUserId: string | null;
  walletAddress: string | null;
  isWorldApp: boolean;
  onClose: () => void;
  onSubmitted: () => void;
};

function formatClaimAmount(status: ClaimStatus | null): string {
  if (!status) return '—';
  return formatOnChainBalance(ethers.formatUnits(status.amount, 18));
}

function formatRemainingTime(totalSeconds: number): string {
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  const clock = [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
  return days > 0 ? `${days}d ${clock}` : clock;
}

function formatDebugValue(
  value: string | boolean | null,
  labels: { notProvided: string; yes: string; no: string },
): string {
  if (value === null || value === '') return labels.notProvided;
  if (typeof value === 'boolean') return value ? labels.yes : labels.no;
  return value;
}

function getWorldIdDebugFields(
  diagnostic: WorldIdDiagnostic,
  t: (key: string) => string,
): Array<{ label: string; value: string }> {
  const summary = diagnostic.debugSummary;
  const labels = {
    notProvided: t('claim.notProvided'),
    yes: t('claim.yes'),
    no: t('claim.no'),
  };
  return [
    { label: t('claim.diagnosticCode'), value: diagnostic.diagnosticCode },
    { label: t('claim.errorCode'), value: diagnostic.errorCode },
    { label: 'IDKit Request ID', value: formatDebugValue(summary?.requestId ?? null, labels) },
    { label: 'generatedAt', value: formatDebugValue(summary?.generatedAt ?? null, labels) },
    { label: 'transport', value: formatDebugValue(summary?.transport ?? null, labels) },
    { label: 'packageVersion', value: formatDebugValue(summary?.packageVersion ?? null, labels) },
    { label: 'mini_app.platform', value: formatDebugValue(summary?.miniApp?.platform ?? null, labels) },
    { label: 'verify_version', value: formatDebugValue(summary?.miniApp?.verifyVersion ?? null, labels) },
    { label: 'send_channel', value: formatDebugValue(summary?.miniApp?.sendChannel ?? null, labels) },
    { label: 'minikit_subscribed', value: formatDebugValue(summary?.miniApp?.minikitSubscribed ?? null, labels) },
    { label: 'response_channel', value: formatDebugValue(summary?.miniApp?.responseChannel ?? null, labels) },
    { label: 'environment', value: diagnostic.environment },
    { label: 'action', value: formatDebugValue(diagnostic.action, labels) },
    { label: t('claim.maskedAppId'), value: diagnostic.appId },
    { label: t('claim.maskedRpId'), value: diagnostic.rpId },
    { label: t('claim.firstVerification'), value: diagnostic.firstVerification ? labels.yes : labels.no },
    { label: t('claim.proofReturned'), value: diagnostic.proofReturned ? labels.yes : labels.no },
    { label: t('claim.backendVerifyAttempted'), value: diagnostic.backendVerifyAttempted ? labels.yes : labels.no },
  ];
}

export default function ClaimModal({
  isOpen,
  authUserId,
  walletAddress,
  isWorldApp,
  onClose,
  onSubmitted,
}: ClaimModalProps) {
  const { t } = useTranslation();
  const authUserIdRef = useRef(authUserId);
  const walletAddressRef = useRef(walletAddress);
  authUserIdRef.current = authUserId;
  walletAddressRef.current = walletAddress;
  const stageLabels: Record<ClaimStage, string> = {
    checking: t('claim.checking'),
    'world-id-verification': t('claim.verification'),
    authorization: t('claim.authorization'),
    'wallet-confirmation': t('claim.walletConfirmation'),
    'transaction-submitted': t('claim.transactionSubmitted'),
    success: t('claim.success'),
  };
  const [status, setStatus] = useState<ClaimStatus | null>(null);
  const [stage, setStage] = useState<ClaimStage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [worldIdDiagnostic, setWorldIdDiagnostic] = useState<WorldIdDiagnostic | null>(null);
  const [isDebugPanelOpen, setIsDebugPanelOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [statusObservedAt, setStatusObservedAt] = useState<{
    clientMs: number;
    effectiveTimestamp: bigint;
  } | null>(null);
  const [clockTick, setClockTick] = useState(() => Date.now());

  const refreshClaimStatus = useCallback(async (address: string) => {
    const nextStatus = await getBbgClaimStatus(address);
    setStatus(nextStatus);
    setStatusObservedAt({ clientMs: Date.now(), effectiveTimestamp: nextStatus.effectiveTimestamp });
    return nextStatus;
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setStatus(null);
    setStage('checking');
    setError(null);
    setWorldIdDiagnostic(null);
    setIsDebugPanelOpen(false);
    setCopyStatus(null);
    setTxHash(null);
    setStatusObservedAt(null);
    if (!walletAddress) {
      setStage(null);
      return;
    }
    void refreshClaimStatus(walletAddress)
      .catch((statusError) => {
        setError(formatErrorWithCode(statusError, t, 'errors.claimStatus'));
      })
      .finally(() => setStage(null));
  }, [isOpen, walletAddress, refreshClaimStatus, t]);

  useEffect(() => {
    if (!isOpen || !walletAddress) return;
    const tickTimer = window.setInterval(() => setClockTick(Date.now()), 1_000);
    const refreshTimer = window.setInterval(() => {
      void refreshClaimStatus(walletAddress).catch(() => undefined);
    }, 15_000);
    return () => {
      window.clearInterval(tickTimer);
      window.clearInterval(refreshTimer);
    };
  }, [isOpen, walletAddress, refreshClaimStatus]);

  const effectiveNow = useMemo(() => {
    if (!status || !statusObservedAt) return status?.effectiveTimestamp ?? 0n;
    if (status.paused) return status.effectiveTimestamp;
    const elapsedSeconds = BigInt(Math.max(0, Math.floor((clockTick - statusObservedAt.clientMs) / 1_000)));
    return statusObservedAt.effectiveTimestamp + elapsedSeconds;
  }, [clockTick, status, statusObservedAt]);

  const cooldownRemainingSeconds = useMemo(() => {
    if (!status || status.lastDirectClaimAt === 0n || status.cooldown === 0n) return 0;
    const nextClaimAt = status.lastDirectClaimAt + status.cooldown;
    const remaining = nextClaimAt > effectiveNow ? nextClaimAt - effectiveNow : 0n;
    return Number(remaining > BigInt(Number.MAX_SAFE_INTEGER) ? BigInt(Number.MAX_SAFE_INTEGER) : remaining);
  }, [effectiveNow, status]);

  if (!isOpen) return null;

  const isBusy = stage !== null && stage !== 'success';
  const hasSucceeded = stage === 'success';
  const canClaim = Boolean(
    walletAddress
    && isWorldApp
    && status?.enabled
    && status.amount > 0n
    && cooldownRemainingSeconds === 0
    && !hasSucceeded,
  );

  const handleClaim = async () => {
    if (!walletAddress) {
      setError(formatMessageWithCode(t('claim.login'), t, 'AUTH_REQUIRED'));
      return;
    }
    if (!isWorldApp) {
      setError(formatMessageWithCode(t('errors.worldAppNativeTransactionRequired'), t, 'WORLD_APP_REQUIRED'));
      return;
    }

    setError(null);
    setWorldIdDiagnostic(null);
    setIsDebugPanelOpen(false);
    setCopyStatus(null);
    setTxHash(null);
    const requestAuthUserId = authUserId;
    const requestWalletAddress = walletAddress;
    try {
      const result = await claimBbg(walletAddress, setStage, {
        enrollment: {
          title: t('claim.enrollmentTransactionTitle'),
          description: t('claim.enrollmentTransactionDescription'),
        },
        directClaim: {
          title: t('claim.transactionTitle'),
          description: t('claim.transactionDescription', { amount: formatClaimAmount(status) }),
        },
        patternClaim: {
          title: t('claim.transactionTitle'),
          description: t('claim.transactionDescription', { amount: formatClaimAmount(status) }),
        },
      });
      setTxHash(result.txHash);
      if (
        requestAuthUserId
        && authUserIdRef.current === requestAuthUserId
        && walletAddressRef.current === requestWalletAddress
      ) {
        await recordWalletOperationBestEffort({
          operation_type: 'claim',
          idempotency_key: buildWalletOperationIdempotencyKey('claim', result.txHash),
          wallet_address: walletAddress,
          reference_type: 'wallet_bbg_claim',
          metadata: { claim_amount_base_units: result.amount?.toString() ?? null },
          legs: [{
            leg_type: 'claim',
            sequence_no: 0,
            chain_id: 480,
            tx_hash: result.txHash,
            user_op_hash: result.userOpHash,
            from_address: walletAppConfig.bubbleClaimContractAddress,
            to_address: walletAddress,
            token_address: ON_CHAIN_TOKEN_ADDRESSES.BBG,
            token_symbol: 'BBG',
            amount_base_units: result.amount?.toString() ?? null,
          }],
        });
      }
      await refreshClaimStatus(walletAddress).catch(() => undefined);
      setStage('success');
      onSubmitted();
    } catch (claimError) {
      setError(formatErrorWithCode(claimError, t, 'errors.claimFailed'));
      setWorldIdDiagnostic(getWorldIdDiagnostic(claimError));
      setStage(null);
    }
  };

  const debugFields = worldIdDiagnostic ? getWorldIdDebugFields(worldIdDiagnostic, t) : [];

  const handleCopyDebug = async () => {
    if (!worldIdDiagnostic) return;
    const text = debugFields.map(({ label, value }) => `${label}: ${value}`).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus(t('claim.copiedDiagnostic'));
    } catch {
      setCopyStatus(t('claim.copyUnsupported'));
    }
  };

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isBusy) onClose();
      }}
    >
      <section className="claim-modal" role="dialog" aria-modal="true" aria-labelledby="claim-title">
        <div className="modal-header">
          <div>
            <h2 id="claim-title">{t('claim.title')}</h2>
          </div>
          <button className="modal-close-button" type="button" onClick={onClose} disabled={isBusy} aria-label={t('claim.close')}>
            ×
          </button>
        </div>

        <div className="claim-reward-card">
          <span>{t('claim.todayReward')}</span>
          <strong>{cooldownRemainingSeconds > 0 ? t('claim.claimed') : `${formatClaimAmount(status)} BBG`}</strong>
        </div>

        {cooldownRemainingSeconds > 0 && (
          <p className="claim-cooldown" role="status">
            {t('claim.nextClaim', { time: formatRemainingTime(cooldownRemainingSeconds) })}
          </p>
        )}

        {!walletAddress && <p className="claim-empty">{t('claim.login')}</p>}
        {walletAddress && !isWorldApp && <p className="claim-empty">{t('claim.worldAppOnly')}</p>}
        {stage && <p className="claim-stage" role="status">{stageLabels[stage]}</p>}
        {error && (
          <div className="send-token-error" role="alert">
            <p>{error}</p>
            {worldIdDiagnostic && (
              <div className="claim-world-id-diagnostics">
                <p>{t('claim.diagnostic', { value: worldIdDiagnostic.diagnosticCode })}</p>
                <p>{t('claim.flow', { value: worldIdDiagnostic.firstVerification ? t('claim.firstVerificationFlow') : t('claim.sessionProof') })}</p>
                <p>{t('claim.errorCode')}: {worldIdDiagnostic.errorCode}</p>
                {worldIdDiagnostic.debugSummary?.requestId && <p>IDKit Request ID: {worldIdDiagnostic.debugSummary.requestId}</p>}
                {worldIdDiagnostic.debugSummary?.transport && <p>{t('claim.transport', { value: worldIdDiagnostic.debugSummary.transport })}</p>}
                {worldIdDiagnostic.debugSummary?.miniApp?.platform && <p>{t('claim.worldAppPlatform', { value: worldIdDiagnostic.debugSummary.miniApp.platform })}</p>}
                {worldIdDiagnostic.debugSummary?.miniApp?.verifyVersion && <p>{t('claim.miniKitVerify', { value: worldIdDiagnostic.debugSummary.miniApp.verifyVersion })}</p>}
                <button className="secondary-button claim-world-id-debug-button" type="button" onClick={() => setIsDebugPanelOpen(true)}>
                  IDKit Debug
                </button>
              </div>
            )}
          </div>
        )}
        {txHash && (
          <a className="send-token-hash" href={`https://worldscan.org/tx/${txHash}`} target="_blank" rel="noreferrer">
            {t('claim.confirmedViewWorldscan')}
          </a>
        )}

        <div className="send-token-actions">
          <button className="auth-button" type="button" onClick={() => void handleClaim()} disabled={isBusy || !canClaim}>
            {isBusy ? t('common.processing') : t('claim.claim', { amount: formatClaimAmount(status) })}
          </button>
        </div>
      </section>
      {isDebugPanelOpen && worldIdDiagnostic && (
        <div
          className="world-id-debug-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsDebugPanelOpen(false);
          }}
        >
          <section className="world-id-debug-dialog" role="dialog" aria-modal="true" aria-labelledby="world-id-debug-title">
            <div className="modal-header">
              <div>
                <p className="eyebrow">DEVELOPMENT ONLY</p>
                <h2 id="world-id-debug-title">IDKit Debug</h2>
              </div>
              <button className="modal-close-button" type="button" onClick={() => setIsDebugPanelOpen(false)} aria-label={t('claim.closeDebug')}>
                ×
              </button>
            </div>
            <p className="world-id-debug-note">{t('claim.safeDiagnosticNote')}</p>
            <dl className="world-id-debug-fields">
              {debugFields.map(({ label, value }) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
            <div className="send-token-actions world-id-debug-actions">
              <button className="secondary-button" type="button" onClick={() => void handleCopyDebug()}>{t('claim.copySafeDiagnostic')}</button>
              <button className="auth-button" type="button" onClick={() => setIsDebugPanelOpen(false)}>{t('common.close')}</button>
            </div>
            {copyStatus && <p className="world-id-debug-copy-status" role="status">{copyStatus}</p>}
          </section>
        </div>
      )}
    </div>
  );
}
